import { Subject, merge, ReplaySubject, } from 'rxjs';
import { distinctUntilChanged, groupBy, mergeMap, throttleTime, publishReplay, } from 'rxjs/operators';
import { invariant, SubscriptionManager } from '@streem/toolbox';
import { UserId } from '@streem/domain-id';
import { isWallItem } from './guards';
import { logger } from '../../util/logger';
export class Wall {
    get tokenExpired() {
        return this._tokenExpired;
    }
    constructor(config) {
        this.config = config;
        this.subscriptions = new SubscriptionManager();
        this.remoteEvents = new Subject();
        this.localEvents = new Subject();
        this.cache = new Map();
        this.wallSyncClient = config.wallSyncClient;
        this.connected = this.wallSyncClient.connected.pipe(distinctUntilChanged());
        this.wallSyncClient.tokenExpired?.subscribe(expired => {
            logger.info(`receiving update to tokenExpired=${expired}`);
            this._tokenExpired = expired;
        });
        this.reconnectFailed = this.wallSyncClient.reconnectFailed;
        this.events = merge(this.wallSyncClient.events, this.localEvents);
        this.itemsById = this.events.pipe(groupBy(wie => wie.itemId, undefined, undefined, () => new ReplaySubject(1)), publishReplay());
        this.itemsByType = this.events.pipe(groupBy(wie => wie.itemType, undefined, undefined, () => new ReplaySubject()), publishReplay());
    }
    connect() {
        this.subscriptions.addSubscriptions([
            this.itemsById.connect(),
            this.itemsByType.connect(),
            this.events.subscribe(event => {
                if (event.type === 'DELETED') {
                    this.cache.delete(event.itemId);
                }
                else if (!this.cache.has(event.itemId)) {
                    const ownerId = isWallItem(event.item)
                        ? new UserId(event.item.ownerUserId)
                        : this.config.localUserId;
                    invariant(ownerId, 'Expected to find an ownerId on wall item');
                    this.cache.set(event.itemId, [ownerId, event.itemType]);
                }
            }),
            this.remoteEvents
                .pipe(groupBy(event => event.itemId), mergeMap(group => group.pipe(throttleTime(500, undefined, { leading: true, trailing: true }))))
                .subscribe({
                next: async (event) => {
                    if (event.type === 'ADDED') {
                        await this.wallSyncClient.add(event.item);
                    }
                    else if (event.type === 'UPDATED') {
                        await this.wallSyncClient.update(event.item.id, event.item);
                    }
                    else if (event.type === 'DELETED') {
                        await this.wallSyncClient.delete(event.itemId);
                    }
                    else {
                        logger.error(`Unexpected eventType=${event.type} for wallItemId=${event.itemId}`);
                    }
                },
            }),
        ]);
        return this.wallSyncClient.connect();
    }
    disconnect() {
        this.subscriptions.unsubscribeAll();
        this.cache.clear();
        this.localEvents.complete();
        this.remoteEvents.complete();
        return this.wallSyncClient.disconnect();
    }
    add(item) {
        if (this.cache.has(item.id)) {
            throw new Error(`WallItem ${item.id} already exists. Please call .update or .addOrUpdate.`);
        }
        this.sendEvent({
            type: 'ADDED',
            itemId: item.id,
            itemType: item.type,
            item,
        });
    }
    update(item) {
        if (!this.cache.has(item.id)) {
            throw new Error(`WallItem wallItemType=${item.type} wallItemId=${item.id} update failed, the item does not exist, add it first`);
        }
        this.assertOwnership(item.id);
        this.sendEvent({
            type: 'UPDATED',
            itemId: item.id,
            itemType: item.type,
            item,
        });
    }
    delete(itemId) {
        const cacheEntry = this.cache.get(itemId);
        if (!cacheEntry) {
            logger.warn(`Failed to delete WallItem wallItemId=${itemId}, does not exist.`);
            return false;
        }
        this.assertOwnership(itemId);
        this.sendEvent({
            type: 'DELETED',
            itemId,
            itemType: cacheEntry[1],
        });
        return true;
    }
    addOrUpdate(item) {
        if (this.cache.has(item.id)) {
            this.update(item);
        }
        else {
            this.add(item);
        }
    }
    sendEvent(event) {
        // don't spam logs on heartbeat.
        const level = event.itemType === 'heartbeat' ? 'debug' : 'info';
        logger[level](`Sending WallItemEvent eventType=${event.type} wallItemType=${event.itemType} wallItemId=${event.itemId}`);
        this.localEvents.next(event);
        this.remoteEvents.next(event);
    }
    assertOwnership(id) {
        if (!this.isOwner(id)) {
            throw new Error('Cannot update an item you are not the owner of');
        }
    }
    isOwner(id) {
        // Users not logged in are the owners of nothing.
        if (!this.config.localUserId)
            return false;
        // New entires are assumed to be owned by the user.
        const cacheEntry = this.cache.get(id);
        if (!cacheEntry)
            return true;
        // Historical entires must be checked
        const entryOwner = cacheEntry[0];
        return this.config.localUserId.compare(entryOwner);
    }
}
//# sourceMappingURL=wall.js.map